/**
 * Utility for generating colors for map markers
 */

/**
 * Generates a pastel color with good visibility based on an index.
 * Uses HSL color space to distribute colors uniformly.
 *
 * @param index - Color index (0-based). Same index = same color
 * @param saturation - Saturation (0-100). Default 70 for vibrant pastels
 * @param lightness - Lightness (0-100). Default 65 for good visibility
 * @returns Color string in hex format (#RRGGBB)
 */
export function getMarkerColor(
    index: number,
    saturation: number = 70,
    lightness: number = 65
): string {
    // Golden ratio for uniform color distribution
    const goldenRatio = 0.618033988749895;

    // Calculate hue distributed uniformly
    const hue = (index * goldenRatio * 360) % 360;

    return hslToHex(hue, saturation, lightness);
}

/**
 * Generates a completely random pastel-toned color
 *
 * @param saturation - Saturation (0-100). Default 70
 * @param lightness - Lightness (0-100). Default 65
 * @returns Color string in hex format (#RRGGBB)
 */
export function getRandomMarkerColor(
    saturation: number = 70,
    lightness: number = 65
): string {
    const hue = Math.random() * 360;
    return hslToHex(hue, saturation, lightness);
}

/**
 * Generates an array of N well-distributed colors
 *
 * @param count - Number of colors to generate
 * @param saturation - Saturation (0-100). Default 70
 * @param lightness - Lightness (0-100). Default 65
 * @returns Array of colors in hex format
 */
export function generateMarkerColorPalette(
    count: number,
    saturation: number = 70,
    lightness: number = 65
): string[] {
    return Array.from({ length: count }, (_, i) =>
        getMarkerColor(i, saturation, lightness)
    );
}

/**
 * Converts HSL to hexadecimal
 *
 * @param h - Hue (0-360)
 * @param s - Saturation (0-100)
 * @param l - Lightness (0-100)
 * @returns Color string in hex format (#RRGGBB)
 */
function hslToHex(h: number, s: number, l: number): string {
    const sNorm = s / 100;
    const lNorm = l / 100;

    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;

    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }

    const rHex = Math.round((r + m) * 255).toString(16).padStart(2, '0');
    const gHex = Math.round((g + m) * 255).toString(16).padStart(2, '0');
    const bHex = Math.round((b + m) * 255).toString(16).padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`;
}

// ============================================
// Predefined Palette (OPTIONAL)
// ============================================

/**
 * Palette of 100 predefined pastel colors with good visibility
 * Generated using getMarkerColor function for consistency
 */
export const MARKER_COLOR_PALETTE: readonly string[] = generateMarkerColorPalette(100);

/**
 * Gets a color from the predefined palette
 *
 * @param index - Color index (cyclic if > 99)
 * @returns Color in hex format
 */
export function getColorFromPalette(index: number): string {
    return MARKER_COLOR_PALETTE[index % MARKER_COLOR_PALETTE.length];
}
